/**
 * Audio Service for handling microphone input and audio playback
 */
class AudioService {
  constructor() {
    this.audioContext = null;
    this.stream = null;
    this.isRecording = false;
    this.isMuted = false; // Muted during AI playback to prevent echo
    this.scriptProcessor = null;
    this.sourceNode = null;
    this.onDataCallback = null;
    this.currentPlayback = null; // Track current AI audio playback
    this.currentPlaybackCancel = null;
    this.currentPlaybackUrl = null;
    this.unmuteTimer = null;
  }

  /**
   * Request microphone access and initialize AudioContext
   */
  async requestMicrophoneAccess() {
    try {
      // Request microphone with specific constraints
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });

      // Create AudioContext with 16kHz sample rate (Vosk requirement)
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });

      return true;
    } catch (error) {
      console.error('Microphone access denied:', error);
      return false;
    }
  }

  /**
   * Start recording audio in RAW PCM format for Vosk
   * @param {Function} onDataAvailable - Callback for audio chunks
   */
  async startRecording(onDataAvailable) {
    if (!this.stream || !this.audioContext) {
      throw new Error('Microphone access not granted');
    }
    if (this.audioContext.state === 'suspended') await this.audioContext.resume();
    if (this.isRecording) return;

    this.onDataCallback = onDataAvailable;
    this.isRecording = true;

    // Create source from microphone stream
    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

    // Create ScriptProcessor for processing audio chunks
    // Buffer size: 4096 samples (256ms at 16kHz)
    const bufferSize = 4096;
    this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

    this.scriptProcessor.onaudioprocess = (event) => {
      if (!this.isRecording || this.isMuted) return;

      const inputData = event.inputBuffer.getChannelData(0);

      // Convert Float32 to Int16 PCM
      const pcmData = this.float32ToInt16(inputData);

      // Convert to base64 for transmission
      const base64 = this.arrayBufferToBase64(pcmData.buffer);

      // Send to callback
      if (this.onDataCallback) {
        this.onDataCallback(base64);
      }
    };

    // Connect nodes
    this.sourceNode.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.audioContext.destination);
  }

  /**
   * Convert Float32Array to Int16Array (PCM format)
   */
  float32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      // Clamp to [-1, 1] and convert to Int16 range
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return int16Array;
  }

  /**
   * Convert ArrayBuffer to base64
   */
  arrayBufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Stop recording audio
   */
  stopRecording() {
    if (this.isRecording) {
      this.isRecording = false;

      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }

      if (this.sourceNode) {
        this.sourceNode.disconnect();
        this.sourceNode = null;
      }
    }
  }

  /**
   * Play audio from base64
   * @param {string} base64Audio - Base64 encoded audio
   */
  /**
   * Mute microphone recording (prevents sending audio chunks during AI playback)
   */
  muteRecording() {
    if (this.unmuteTimer) {
      clearTimeout(this.unmuteTimer);
      this.unmuteTimer = null;
    }
    this.isMuted = true;
  }

  /**
   * Unmute microphone recording
   */
  unmuteRecording() {
    this.isMuted = false;
  }

  cancelCurrentPlayback(reason = 'Audio playback canceled') {
    if (this.currentPlaybackCancel) {
      const error = new Error(reason);
      error.code = 'PLAYBACK_CANCELED';
      this.currentPlaybackCancel(error);
      return;
    }

    if (this.currentPlayback) {
      this.currentPlayback.pause();
      this.currentPlayback = null;
    }

    if (this.currentPlaybackUrl) {
      URL.revokeObjectURL(this.currentPlaybackUrl);
      this.currentPlaybackUrl = null;
    }
  }

  async playAudio(base64Audio, { durationMs = null } = {}) {
    try {
      // A previous rejected play() call can otherwise remain pending forever and
      // block subsequent replies. Always settle it before starting new audio.
      this.cancelCurrentPlayback('Audio playback replaced by a newer response');

      // Mute mic to prevent echo capture
      this.muteRecording();

      const audioData = atob(base64Audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);

      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }

      const blob = new Blob([arrayBuffer], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(blob);

      const audio = new Audio(audioUrl);
      audio.preload = 'auto';
      audio.playsInline = true;
      this.currentPlayback = audio;
      this.currentPlaybackUrl = audioUrl;

      return new Promise((resolve, reject) => {
        let settled = false;
        let playbackTimeout = null;

        const startTimeout = window.setTimeout(() => {
          const error = new Error('Audio playback did not start within 10 seconds');
          error.code = 'PLAYBACK_START_TIMEOUT';
          finish(error);
        }, 10000);

        const finish = (error = null) => {
          if (settled) return;
          settled = true;

          window.clearTimeout(startTimeout);
          if (playbackTimeout) window.clearTimeout(playbackTimeout);

          audio.onplaying = null;
          audio.onended = null;
          audio.onerror = null;

          if (error) audio.pause();
          URL.revokeObjectURL(audioUrl);

          if (this.currentPlayback === audio) {
            this.currentPlayback = null;
            this.currentPlaybackCancel = null;
            this.currentPlaybackUrl = null;
          }

          if (error) {
            this.unmuteRecording();
            reject(error);
            return;
          }

          // Unmute mic after playback with a small delay to avoid capturing tail echo.
          this.unmuteTimer = window.setTimeout(() => {
            this.unmuteTimer = null;
            this.unmuteRecording();
          }, 300);
          resolve();
        };

        this.currentPlaybackCancel = finish;

        audio.onplaying = () => {
          window.clearTimeout(startTimeout);
          if (playbackTimeout) window.clearTimeout(playbackTimeout);
          const timeoutMs = Number.isFinite(audio.duration)
            ? Math.ceil(audio.duration * 1000) + 5000
            : Number.isFinite(durationMs) ? durationMs + 5000 : 60000;
          playbackTimeout = window.setTimeout(() => {
            const error = new Error('Audio playback did not finish in time');
            error.code = 'PLAYBACK_FINISH_TIMEOUT';
            finish(error);
          }, Math.max(timeoutMs, 10000));
        };
        audio.onended = () => finish();
        audio.onerror = () => {
          const error = new Error('Browser could not play the AI audio response');
          error.code = 'PLAYBACK_MEDIA_ERROR';
          finish(error);
        };

        // play() rejects (not onerror) when autoplay policy or device state
        // blocks playback. Propagating that rejection prevents a silent hang.
        const playPromise = audio.play();
        if (playPromise?.catch) {
          playPromise.catch(finish);
        }
      });
    } catch (error) {
      console.error('Error playing audio:', error);
      this.unmuteRecording();
      throw error;
    }
  }

  /**
   * Clean up resources
   */
  cleanup() {
    this.cancelCurrentPlayback('Audio service cleanup');
    this.stopRecording();

    if (this.unmuteTimer) {
      clearTimeout(this.unmuteTimer);
      this.unmuteTimer = null;
    }
    this.unmuteRecording();

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

export default new AudioService();
